import { createRouter, createWebHistory } from 'vue-router';
import CarrierDashboard from '@/views/carrier/Dashboard.vue';
// import BrokerDashboard from '@/views/broker/Dashboard.vue'
import Login from '@/views/login/Login.vue';
import LoginWithEmail from '@/views/login/LoginWithEmail.vue';
import LoginVerification from '@/views/login/Verification.vue';
import LoginWithHighway from '@/views/LoginWithHighway.vue';
import AccessPending from '@/views/common/AccessPending.vue';
import OAuthAuthorizations from '@/views/common/oauth/Authorizations.vue';
import PrivateModeBlock from '@/views/common/PrivateModeBlock.vue';
import LegalAgreements from '@/views/LegalAgreements.vue';
import Logout from '@/views/common/Logout.vue';
import store from '../store';
import dispatcher from './dispatcher';
import carrier from './carrier';
import signup from './signup';
import SSODemo from '@/views/SSODemo.vue';
import LoadBoardSSO from '@/views/LoadBoardSSO.vue';
import security from './security';

const routes = [
  // Do not remove this.  It handles when a user gets redirected
  // after logging in before they are attached to a carrier
  {
    path: '/',
    name: 'Home',
    component: CarrierDashboard,
    meta: { pageTitle: 'Home' },
  },

  // Session paths
  {
    path: '/login',
    name: 'Login',
    component: Login,
    redirect: { name: 'CarrierDetails' },
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/login-with-email',
    name: 'LoginWithEmail',
    component: LoginWithEmail,
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/oauth/authorizations',
    name: 'OAuth Authorizations',
    component: OAuthAuthorizations,
    meta: { pageTitle: 'OAuth Authorization' },
  },
  // Login Flow
  {
    path: '/login/code',
    name: 'LoginVerification',
    component: LoginVerification,
    meta: { pageTitle: 'Login Code', doesNotRequireAuthentication: true },
  },
  {
    path: '/login-with-highway',
    name: 'LoginWithHighway',
    component: LoginWithHighway,
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/single-sign-on-demo',
    name: 'SSODemo',
    component: SSODemo,
    meta: { pageTitle: 'SSO Demo', doesNotRequireAuthentication: true },
  },
  {
    path: '/single-sign-on-demo/loadboard',
    name: 'LoadBoard SSO Demo',
    component: LoadBoardSSO,
    meta: {
      pageTitle: 'LoadBoard SSO Demo',
      doesNotRequireAuthentication: true,
    },
  },
  {
    path: '/access-pending',
    name: 'AccessPending',
    component: AccessPending,
    meta: {
      pageTitle: 'Access Pending',
      doesNotRequireAuthentication: true,
      doesNotRequireCompany: true,
    },
  },
  {
    path: '/private-mode-block',
    name: 'PrivateModeBlock',
    component: PrivateModeBlock,
    meta: {
      pageTitle: 'Private Browsing Detected',
      doesNotRequireAuthentication: true,
      doesNotRequireCompany: true,
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
    meta: { pageTitle: 'Logout' },
  },
  {
    path: '/legal-agreements',
    name: 'LegalAgreements',
    component: LegalAgreements,
    meta: {
      pageTitle: 'Legal Agreements',
      doesNotRequireAuthentication: false,
      doesNotRequireCompany: true,
    },
  },
  ...signup,
  ...carrier,
  ...dispatcher,
  ...security,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    meta: { pageTitle: 'Page Not Found' },
    component: () => import('../views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  let title = 'Highway - ' + (to.meta.pageTitle || to.name);

  if (to.name === 'Load Detail') {
    title = `Load: ${to.params.id}`;
  }

  document.title = title;
  await store.dispatch('users/refreshCurrentUser');
  let currentUser = store.state.users.currentUser;
  let currentCompany = currentUser?.company;
  let companyType = currentCompany?.company_type;
  let homePage;

  if (
    localStorage.getItem('hw_register_device_pending') &&
    currentCompany?.id &&
    to.name !== 'SecurityRegisterDevice'
  ) {
    localStorage.removeItem('hw_register_device_pending');
    await store.dispatch('users/userSignOut');
    return next({ name: 'SignUp' });
  }

  await Promise.all([
    store.dispatch('users/refreshCurrentFeatureFlags', currentUser),
    store.dispatch('users/getUserInquiries', currentUser?.id),
    store.dispatch('users/refreshCurrentLoadBoardAccess'),
  ]);

  // Initialize Grafana Faro if feature flag is enabled
  const { initializeGrafanaFaroIfEnabled } = await import('@/services/grafana');
  initializeGrafanaFaroIfEnabled(store.state.users.featureFlags);

  const enableRateConfirmation =
    store.state.users.featureFlags?.enable_rate_confirmation_page?.enabled;

  const enforceIdvChecksForExistingUsers =
    store.state.users.featureFlags?.enforce_idv_checks_for_existing_users
      ?.enabled;

  const canViewMyLoadsScope = store.state.users.currentUser?.scopes?.includes(
    'carrier_load_abilities'
  );

  const userIsADriver = store.getters['users/userIsADriver'];

  const enableCarrierLoadBoard =
    store.state.users.currentUser?.scopes?.includes(
      'carrier_view_posted_loads'
    );

  const enableOffers =
    store.state.users.featureFlags?.fe_enable_offers_management?.enabled &&
    enableCarrierLoadBoard;

  let isIDVOnboardingActive = false;
  let isIDVOnboardedActive = false;

  const status = store.state.users.currentUser?.identity_proofing_status;
  const isIdentityVerified =
    store.state.users.currentUser?.last_verified_identity_at;

  if (isIdentityVerified == null) {
    if (status === 'skip_identity_proofing') {
      // Skip IDV
      isIDVOnboardingActive = false;
      isIDVOnboardedActive = false;
    } else if (status === 'require_identity_proofing') {
      // Force IDV
      isIDVOnboardingActive = true;
      isIDVOnboardedActive = true;
    } else {
      // Fallback to the feature flags if the status is neither 1 nor 2
      isIDVOnboardingActive = !!store.getters['users/getFeatureFlag']?.(
        'idv_onboarding_flow'
      )?.['enabled'];
      isIDVOnboardedActive =
        !!store.getters['users/getFeatureFlag']?.('idv_onboarded_flow')?.[
          'enabled'
        ];
    }
  }

  let isOnboardingEmailVerificationActive = store.getters[
    'users/getFeatureFlag'
  ]?.('onboarding_email_verification')?.['enabled'];

  let isCarrierProductEnabled =
    store.getters['users/getFeatureFlag']?.('carrier_product')?.['enabled'];

  let latestInquiryStatus = store.getters['users/inquiryStatus'];

  // If a message was passed from the server in the query param message,
  // then store in localStorage so that it can be showed when the page
  // finishes loading
  if (to.query && to.query.message?.length > 0) {
    // Not storing the serverToastMessage when the user hits Verify Equipment button in Rules Assessment Status, because we need to
    // display the toast in the next render, not in the next refresh.
    if (
      !(
        from.name === 'Carrier Rules Assessment Status' &&
        to.name === 'CarrierWelcomePage'
      )
    ) {
      localStorage.setItem('serverToastMessage', to.query.message);
    }
  }

  if (to.fullPath?.includes('sso_demo')) {
    console.log('SSO Demo Redirect');
    const url = new URL(window.location.origin + to.fullPath);

    url.searchParams.delete('sso_demo');

    localStorage.setItem(
      'redirect_sso_demo',
      url.pathname + '?' + url.searchParams.toString()
    );

    next({ name: 'SSODemo' });
  }

  if (!enableRateConfirmation) {
    const restrictedRoutes = ['Rate Confirmations', 'Rate Confirmation'];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'CarrierWelcomePage' });
    }
  }

  if (!userIsADriver && !canViewMyLoadsScope) {
    const restrictedRoutes = ['My Loads'];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'CarrierWelcomePage' });
    }
  }

  if (!enableCarrierLoadBoard && currentUser) {
    const restrictedRoutes = ['Load Board', 'Capacity', 'Book Now'];

    if (restrictedRoutes.includes(to.name)) {
      if (!userLoadBoard?.has_completed_idv) {
        return next({ name: 'Protect Your Identity Exchange' });
      }
      if (!userLoadBoard?.has_eld_connected_and_active) {
        return next({ name: 'Connect Your ELD Exchange' });
      }

      return next({ name: 'File Your Insurance Exchange' });
    }
  }

  if (!enableOffers) {
    const restrictedRoutes = ['Offers'];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'CarrierWelcomePage' });
    }
  }

  if (!isCarrierProductEnabled) {
    // List the route names where this rule applies
    const restrictedRoutes = [
      'Carrier Brokers Search Results',
      'Carrier Brokers Show Page',
    ];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'CarrierWelcomePage' });
    }
  }
  const userLoadBoard = store.state.users?.userLoadBoardAccess?.user || {};
  const eldStatus = store.state.carriers.carrierVehiclesForCarrier?.eld_status;
  // const flags = store.state.users.featureFlags || {};
  const carrierId =
    store.state.users.currentUser?.company?.carrier_id ||
    store.state.users.currentUser?.requested_carrier_id;

  const hasAccessToExchange =
    userLoadBoard.has_active_coi_on_file &&
    userLoadBoard.has_completed_idv &&
    userLoadBoard.has_eld_connected_and_active &&
    (userLoadBoard.cargo_policy_on_file_and_active ||
      userLoadBoard.has_pending_cargo_policy_request);

  const eldStatusRequiresResolution = [
    'no_eld_vin_matches_on_coi',
    'no_vins_on_coi',
    'missing_vins_on_coi',
    'bipd_not_on_file',
    'no_coi',
    'no_active_vehicles_found_on_eld',
    'material_difference_between_coi_and_eld',
  ].includes(eldStatus);

  if (
    to.path.includes('/carrier/exchange/access') &&
    !['Protect Your Identity Exchange', 'Benefits'].includes(to.name)
  ) {
    if (hasAccessToExchange) {
      await store.dispatch(
        'carriers/refreshCarrierVehiclesForCarrier',
        carrierId
      );
      if (eldStatusRequiresResolution) {
        return next({ name: 'Protect Your Identity Exchange' });
      }

      return next({ name: 'Load Board' });
    }
  }

  let isAccountOverviewRedesignEnabled = store.getters[
    'users/getFeatureFlag'
  ]?.('display_carrier_my_account_ui')?.['enabled'];

  if (!isAccountOverviewRedesignEnabled) {
    // List the route names where this rule applies
    const restrictedRoutes = [
      'Account Overview',
      'My Account Landing',
      'My Profile',
      'Users Overview',
      'Dispatchers',
      'Ownership',
      'Contacts',
      'RateCon Notifications',
    ];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'CarrierWelcomePage' });
    }
  } else {
    if (to.name === 'Account Overview') {
      return next({ name: 'My Profile' });
    }
  }

  // Need to move this up sooner in the process
  // so that the carrier doesn't get redirected away before we can
  // save them
  await storeOauthParams(to);

  switch (companyType) {
    case 'carrier':
      homePage = { name: 'CarrierWelcomePage' };
      break;
    // case 'broker':
    // homePage = {name: 'Broker Dashboard'}
    // window.open('/onboarding/broker');
    // break
    case 'dispatcher':
      homePage = { name: 'Dispatcher Dashboard' };
      break;

    // Allow brokers to login and claim profiles
    case 'broker':
      homePage = { name: 'CarrierWelcomePage' };
      // await storeOauthParams(to);
      return await handleBrokerUser(
        currentUser,
        currentCompany,
        to,
        from,
        next
      );
    // window.location.pathname = '/broker';
    // window.location.href = `${process.env.API_URL}/broker`;
    // window.location = `${process.env.API_URL}/broker`;
    // break;
  }

  if (
    process.env.NODE_ENV === 'development' &&
    to.fullPath === '/login-with-highway'
  ) {
    return next();
  }

  const managerUserExists = (element) => element === 'manage_users';
  const managerUserScope = currentUser?.scopes.some(managerUserExists);
  if (!managerUserScope && to.path.includes('/users')) {
    // Handle user company requests where the user is not
    // assigned a jwt
    if (!currentUser && to.path.includes('/carrier/users')) {
      localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
      sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
      return next({ name: 'SignUp' });
    } else {
      //If a user doesn't have manage_users as a scope. Redirect to the home
      window.location.pathname = '/';
    }
  }

  // Account is locked -- redirect to holding tank view.  Only
  // becomes unlocked when they have a company associated with them
  if (
    currentUser?.company_verification_status.includes('locked') &&
    !currentUser?.company?.id
  ) {
    console.log('user verification status locked; redirecting');
    if (to.name == 'AccessPending') {
      return next();
    } else {
      return next({ name: 'AccessPending' });
    }
  }
  var isEmailRequiredForLogin =
    store.state.carriers?.isEmailRequiredForLogin || false;

  // User has already submitted a carrier request and verified phone,
  // but has not entered first_name/email
  if (
    currentUser?.requested_carrier_id &&
    !currentUser?.first_name &&
    to.meta.doesNotRequireAuthentication &&
    !isEmailRequiredForLogin &&
    !isIDVOnboardingActive
  ) {
    console.log(
      'has submitted carrier request but not verified phone; redirecting'
    );
    return next({ name: 'CompanyType' });
  }

  // Pages that do not require the user to be logged in
  if (to.meta.doesNotRequireAuthentication) {
    console.log('page does not require authentication');
    // if they are logged in, take them to their home page
    //if (currentUser?.id > 0) { return next(homePage)}
    // if they are not logged in, let them get to the page

    return next();
  }

  if (latestInquiryStatus === 'under_review' && isIDVOnboardingActive) {
    next({ name: 'VerificationFail' });
  }

  // Try and get the user from state and if not in state,
  // authenticate with the server to get the authenticated user
  if (currentUser?.id == null) {
    console.log('user is not logged in; save to redirect_url', to);

    // Only save if going to a path that is the dashboard or OAuth
    if (
      to.fullPath?.includes('oauth') ||
      to.fullPath?.includes('carrier') ||
      to.fullPath?.includes('dispatcher') ||
      to.fullPath?.includes('broker')
    ) {
      localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
      sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    }
    // If the initial path was oauth, then take them
    // to sign up vs. login.  This may need to change after a certain
    // amount of market adoption and when most carriers have a login
    if (to.fullPath?.includes('oauth')) {
      next({ name: 'SignUp' });
    } else {
      // return next({ name: "Login" });
      return next({ name: 'SignUp' });
    }
  }

  if (
    !store.getters['users/hasConsentedToLegalAgreements'] &&
    to.name !== 'LegalAgreements'
  ) {
    console.log('user has not consented to legal agreements; redirecting');

    const wouldGoToClaimMyProfile =
      (currentUser.phone_e164 == null && !isEmailRequiredForLogin) ||
      currentUser.first_name == null;

    const shouldRedirectToIdv =
      isIDVOnboardingActive && wouldGoToClaimMyProfile;

    return next({
      name: 'LegalAgreements',
      query: {
        redirectTo: shouldRedirectToIdv ? 'IdentityVerification' : undefined,
      },
    });
  }

  // The page does not require that we validate
  // that a user is associated with a company
  if (to.meta.doesNotRequireCompany) {
    console.log('view does not require company');
    return next();
  }

  /**
   * Determines if the user should proceed to the next step in the onboarding flow.
   *
   * This function checks multiple conditions related to the current session, company data,
   * onboarding status, user information, and verification statuses to decide if the
   * identity verification onboarding should redirect the user. It prevents progression if
   * email verification is required and the user's email is not yet verified.
   *
   * If all conditions are met, the function retrieves the redirect path after IDV approval
   * and navigates to it.
   *
   * @returns {void} - Redirects the user if conditions are met, or exits if any condition fails.
   */
  const isNewSession = !from.name;
  if (
    !currentCompany?.id &&
    isNewSession &&
    isIDVOnboardingActive &&
    currentUser.email &&
    currentUser.preferred_name &&
    !(isOnboardingEmailVerificationActive && !currentUser?.is_verified_email)
  ) {
    const redirectAfterIDV =
      store.getters['identityVerification/getRedirectAfterIDV'];
    console.log('New Session', redirectAfterIDV, latestInquiryStatus);

    if (
      latestInquiryStatus === 'approved' &&
      (redirectAfterIDV.name || redirectAfterIDV.path)
    ) {
      next(
        redirectAfterIDV.name
          ? { name: redirectAfterIDV.name }
          : redirectAfterIDV.path
      );
    }
  }

  if (
    isIDVOnboardingActive &&
    !companyType &&
    latestInquiryStatus !== 'approved' &&
    latestInquiryStatus !== 'declined'
  ) {
    console.log('Redirect user to IDV Sign up');
    return next({ name: 'IdentityVerification' });
  }

  if (
    isIDVOnboardingActive &&
    !companyType &&
    latestInquiryStatus == 'approved' &&
    currentUser?.email &&
    !currentUser?.is_verified_email &&
    isOnboardingEmailVerificationActive
  ) {
    console.log('Redirect user to Email Verification');
    return next({ name: 'EmailVerificationPending' });
  }

  // Does not have a company
  if (!currentCompany?.id) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user does not have a company; redirecting');
    // Should we take them here or to General Information
    return next({ name: 'CompanyType' });
  }

  // The page does not require we validate that
  // a user has completed their metadata information
  if (to.meta.doesNotRequireCompletedUser) {
    console.log('view does not required meta data to be completed');
    return next();
  }

  if (isIDVOnboardingActive && !companyType && !currentUser?.preferred_name) {
    console.log('Redirect to user to Preferred Name');
    return next({ name: 'PreferredName' });
  }
  // Does not have an email address
  if (currentUser.email == null) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user email is blank; redirecting');
    if (isIDVOnboardingActive) {
      return next({ path: '/sign-up/company-email' });
    }
    return next({ name: 'Carrier Claim My Profile' });
  }

  // Does not have a phone
  if (currentUser.phone_e164 == null && !isEmailRequiredForLogin) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user phone is blank; redirecting');
    return next({ name: 'Carrier Claim My Profile' });
  }

  // Does not have a name
  if (currentUser.first_name == null) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user first_name is blank; redirecting');
    return next({ name: 'Carrier Claim My Profile' });
  }

  // Selfie IDV check: if user requires selfie and their selfie inquiry
  // is under_review, redirect to VerificationFail.
  // Declined selfie inquiries are NOT routed here — App.vue's showSelfieFlow
  // handles retries by calling getSelfieInquiry again (backend creates a new inquiry).
  const isSelfieRequired =
    store.state.users.featureFlags?.enable_selfie_check?.enabled;

  if (isSelfieRequired) {
    const selfieStatus = currentUser?.latest_selfie_status;
    if (selfieStatus === 'under_review') {
      return next({ name: 'VerificationFail' });
    }
  }

  // So at this point, the user is logged in, belongs to a company, has completed
  // all their user profile information, so lets make sure that if they are
  // company type broker they go to a broker endpoint, carrier to a carrier
  // and dispatcher to dispatcher
  if (isIDVOnboardedActive) {
    if (latestInquiryStatus === 'declined') {
      next({ name: 'VerificationFail' });
      return;
    }

    if (
      latestInquiryStatus !== 'approved' &&
      latestInquiryStatus !== 'declined'
    ) {
      console.log('IDV-Modal: Enable IDV Modal');
      store.commit('identityVerification/setShowIDVFlow', true);
    } else if (!currentUser?.preferred_name) {
      console.log('IDV-Modal: Enable preferred name');
      store.commit('identityVerification/setPreferredNameModal', true);
    } else if (!currentUser?.is_verified_email) {
      console.log('IDV-Modal: Enable email verification');
      store.commit('identityVerification/setIsCompanyEmailModal', true);
    }
  }

  // If an existing user who was registered before we enforced the 100% idv_onboarding_flow flipper (identity engine)
  //  completed IDV but never finished preferred name and email verification, we want to reinforce this with this check and
  //  ensure that approved users have both a preferred name and a confirmed email.
  if (
    enforceIdvChecksForExistingUsers &&
    (isIdentityVerified || latestInquiryStatus === 'approved')
  ) {
    if (!currentUser?.preferred_name) {
      console.log('IDV-Modal: Enable preferred name');
      store.commit('identityVerification/setPreferredNameModal', true);
    } else if (!currentUser?.is_verified_email) {
      console.log('IDV-Modal: Enable email verification');
      store.commit('identityVerification/setIsCompanyEmailModal', true);
    }
  }

  // Allow brokers to onboard
  // if ((to.path.includes('/carrier/') || to.path == '/carrier') && !['carrier','broker'].includes(companyType)){
  if (
    (to.path.includes('/carrier/') || to.path == '/carrier') &&
    companyType != 'carrier'
  ) {
    console.log('/carrier namespace guard; redirecting');
    return next(homePage);
  }

  // if ((to.path.includes('/broker/') || to.path == '/broker') && companyType != 'broker'){
  //   console.log('/broker namespace guard; redirecting')
  //   return next(homePage)
  // }

  if (
    (to.path.includes('/dispatcher/') || to.path == '/dispatcher') &&
    companyType != 'dispatcher'
  ) {
    console.log('/dispatcher namespace guard; redirecting');
    return next(homePage);
  }

  if (to.path == '/onboarding' || to.path == '/') {
    console.log('redirect root to home page');
    return next(homePage);
  }

  const ineligibleTFXRoutes = ['Load Board', 'Load Detail', 'Capacity'];
  if (ineligibleTFXRoutes.includes(to.name) && !hasAccessToExchange) {
    console.log('redirect TFX Benefits route');

    if (!userLoadBoard?.has_completed_idv) {
      return next({ name: 'Protect Your Identity Exchange' });
    }
    if (!userLoadBoard?.has_eld_connected_and_active) {
      return next({ name: 'Connect Your ELD Exchange' });
    }

    return next({ name: 'File Your Insurance Exchange' });
  }

  if (
    to.name === 'Benefits' &&
    hasAccessToExchange &&
    userLoadBoard?.has_viewed_tfx_get_started
  ) {
    console.log('Eligible TFX user, bypassing Get Started page');
    return next({ name: 'Load Board' });
  }

  // Restrict driver users to only allowed routes
  if (userIsADriver && currentUser) {
    const allowedRoutes = ['My Loads', 'Load Detail', 'Carrier My Profile'];
    if (
      !to.meta.doesNotRequireAuthentication &&
      !allowedRoutes.includes(to.name)
    ) {
      console.log(
        'Driver user attempted to access non-allowed route; redirecting'
      );
      return next({ name: 'My Loads' });
    }
  }

  if (to.fullPath.includes('/carrier/capacity#suggested')) {
    await store.dispatch('activities/postActivity', {
      user_activity: {
        is_type: 'potential_capacity_viewed',
      },
    });
  }

  console.log('all guards cleared; sending to', to);
  // you made it.  congrats bro.
  return next();
});

async function handleBrokerUser(currentUser, currentCompany, to, from, next) {
  let isActiveBrokerUserNotAssociatedWithCarrier =
    currentUser &&
    currentCompany &&
    currentCompany.company_type == 'broker' &&
    currentCompany.carrier_id == null;

  console.log('a broker user that is already logged in');
  // Handle a logged in broker user that is trying to claim a carrier
  if (
    isActiveBrokerUserNotAssociatedWithCarrier &&
    currentUser.requested_carrier_id &&
    !to.meta.allowBrokerUserToAccess
  ) {
    console.log(to);
    console.log(
      'a broker user that is already logged in and attempting to claim a carrier'
    );
    return next({ name: 'FMSCAAccountVerification' });
  }

  // Handle an existing broker user that has not associated themselves with a company
  // and needs to select a carrier
  if (
    isActiveBrokerUserNotAssociatedWithCarrier &&
    currentUser.requested_carrier_id == null &&
    to.name != 'CarrierDetails'
  ) {
    console.log(to);
    console.log('broker user that has not been associated with a carrier');
    return next({ name: 'CarrierDetails' });
  }

  console.log('all guards cleared; sending to', to);
  return next();
}

// Store the oauth params in local storage if they are present

async function storeOauthParams(to) {
  const { query, fullPath } = to;
  // 1. store every non-empty query param in localStorage
  Object.entries(query).forEach(([key, val]) => {
    if (val != null && val !== '') {
      console.log(`storing oauth ${key}`);
      localStorage.setItem(key, val);
    }
  });

  // 2. if we got a new client_id but no classification_ids, nuke the old ones
  if ('client_id' in query && !('classification_ids' in query)) {
    console.log('removing old oauth classification_ids');
    localStorage.removeItem('classification_ids');
  }

  // 3. capture full route on oauth/authorizations in sessionStorage
  if (fullPath.includes('/oauth/authorizations')) {
    sessionStorage.setItem(
      'redirect_oauth',
      JSON.stringify(JSON.stringify(to))
    );
  }
}

export default router;
